import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { Map as MapInstance } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { List } from "@phosphor-icons/react";

import { useTranslation } from "react-i18next";
import { MapActionSheet } from "../../../components/map/MapActionSheet";
import { MapShell } from "../../../components/map/MapShell";
import { useBaseMapInstance } from "../../../components/map/useBaseMapInstance";
import { createMarkerWithClass, focusOnPoint } from "../../../components/map/mapMarkers";
import { useMapExpansion } from "../../../components/map/useMapExpansion";
import { mapFrameClasses } from "../../../components/map/mapFrameClasses";

import { defaultIconProps } from "../../shared/ui/icons";

type ControlPointMapProps = {
  latitude?: number | null;
  longitude?: number | null;
  label?: string;
  isLoading?: boolean;
  loadingMessage: string;
  emptyMessage: string;
  panelTitle: string;
  extraPanelContent?: ReactNode;
};

const MAP_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const normalizeLatitude = (value?: number | null): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value >= -90 && value <= 90 ? value : null;
};

const normalizeLongitude = (value?: number | null): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return value >= -180 && value <= 180 ? value : null;
};

const fabButtonClasses = mapFrameClasses.fabButton;
const markerClasses =
  "flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-700 shadow-[0_8px_20px_rgba(37,99,235,0.25)] ring-2 ring-white/80 ring-offset-0";
const markerDotClasses = "h-1.5 w-1.5 rounded-full bg-white";
const placeholderClasses =
  "absolute inset-0 flex items-center justify-center rounded-2xl bg-slate-950/30 text-sm font-semibold text-white backdrop-blur-sm";
const labelClasses = "text-sm font-semibold text-slate-900 dark:text-slate-100";
const statsWrapperClasses =
  "grid grid-cols-2 gap-3 text-sm text-slate-700 dark:text-slate-200";
const statsKeyClasses =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400";
const statsValueClasses = "text-sm font-semibold text-slate-900 dark:text-slate-50";

export function ControlPointMap({
  latitude,
  longitude,
  label,
  isLoading = false,
  loadingMessage,
  emptyMessage,
  panelTitle,
  extraPanelContent,
}: ControlPointMapProps) {
  const { t } = useTranslation();
  const mapInstanceRef = useRef<MapInstance | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const hasCenteredMarkerRef = useRef(false);

  const { containerRef: mapContainerRef, status: mapStatus, resize } = useBaseMapInstance({
    style: MAP_STYLE,
    mapRefOverride: mapInstanceRef,
  });

  const {
    isExpanded,
    toggle: toggleExpansion,
    collapse: collapseExpansion,
  } = useMapExpansion();
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const safeLatitude = normalizeLatitude(latitude);
  const safeLongitude = normalizeLongitude(longitude);
  const hasCoordinates =
    typeof safeLatitude === "number" && typeof safeLongitude === "number";

  useEffect(() => {
    resize();
  }, [isExpanded, resize]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapStatus.isMapReady) {
      return;
    }

    if (!hasCoordinates) {
      markerRef.current?.remove();
      markerRef.current = null;
      hasCenteredMarkerRef.current = false;
      return;
    }

    const target: [number, number] = [
      safeLongitude as number,
      safeLatitude as number,
    ];
    if (!markerRef.current) {
      markerRef.current = createMarkerWithClass(map, target, markerClasses, (el) => {
        const dot = document.createElement("div");
        dot.className = markerDotClasses;
        el.appendChild(dot);
      });
    } else {
      markerRef.current.setLngLat(target).addTo(map);
    }

    focusOnPoint(map, target, isExpanded ? 160 : 56, {
      durationMs: hasCenteredMarkerRef.current ? 450 : 0,
      maxZoom: 18,
    });
    hasCenteredMarkerRef.current = true;
  }, [hasCoordinates, isExpanded, mapStatus.isMapReady, safeLatitude, safeLongitude]);

  const formattedLatitude = useMemo(() => {
    if (!hasCoordinates) {
      return "—";
    }
    return (safeLatitude as number).toFixed(8);
  }, [hasCoordinates, safeLatitude]);

  const formattedLongitude = useMemo(() => {
    if (!hasCoordinates) {
      return "—";
    }
    return (safeLongitude as number).toFixed(8);
  }, [hasCoordinates, safeLongitude]);

  const panelLabel =
    label && label.trim().length > 0
      ? label.trim()
      : t("admin.tie_points.form.map_panel_fallback");

  useEffect(() => {
    if (!hasCoordinates && isExpanded) {
      collapseExpansion();
    }
  }, [hasCoordinates, isExpanded, collapseExpansion]);

  useEffect(() => {
    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, []);

  return (
    <>
      <MapShell
        mapContainerRef={mapContainerRef}
        mapClassName={[
          "transition-opacity duration-500",
          mapStatus.hasRenderedTiles ? "opacity-100" : "opacity-0",
        ]
          .filter(Boolean)
          .join(" ")}
        status={mapStatus}
        loadingLabel={isLoading ? loadingMessage : undefined}
        isExpanded={isExpanded && hasCoordinates}
        onToggle={toggleExpansion}
        toggleLabels={{
          expandButton: t("workspaces.lot_detail.map_expand_button"),
          collapseButton: t("workspaces.lot_detail.map_collapse_button"),
          expandAria: t("workspaces.lot_detail.map_expand_aria"),
          collapseAria: t("workspaces.lot_detail.map_collapse_aria"),
        }}
        disableToggle={!hasCoordinates}
        fabAriaLabel={t("admin.tie_points.form.map_panel_title")}
        onZoomIn={() => {
          const map = mapInstanceRef.current;
          if (!map) return;
          const zoom = map.getZoom();
          if (!Number.isFinite(zoom)) return;
          map.easeTo({ zoom: zoom + 1, duration: 200 });
        }}
        onZoomOut={() => {
          const map = mapInstanceRef.current;
          if (!map) return;
          const zoom = map.getZoom();
          if (!Number.isFinite(zoom)) return;
          map.easeTo({ zoom: zoom - 1, duration: 200 });
        }}
        zoomAria={{
          in: t("workspaces.lots.map.zoom_in", "Zoom in"),
          out: t("workspaces.lots.map.zoom_out", "Zoom out"),
        }}
        fabContent={
          <button
            type="button"
            className={fabButtonClasses}
            onClick={() => setIsInfoOpen(true)}
            aria-label={t("admin.tie_points.form.map_panel_title")}
          >
            <List {...defaultIconProps} aria-hidden />
          </button>
        }
        placeholder={
          (!hasCoordinates || isLoading) && (
            <div className={placeholderClasses} role="status">
              {isLoading ? loadingMessage : emptyMessage}
            </div>
          )
        }
      />
      <MapActionSheet
        open={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
        title={panelTitle}
      >
        <p className={labelClasses}>{panelLabel}</p>
        <dl className={statsWrapperClasses}>
          <div>
            <dt className={statsKeyClasses}>{t("common.latitude")}</dt>
            <dd className={statsValueClasses}>{formattedLatitude}</dd>
          </div>
          <div>
            <dt className={statsKeyClasses}>{t("common.longitude")}</dt>
            <dd className={statsValueClasses}>{formattedLongitude}</dd>
          </div>
        </dl>
        {extraPanelContent}
      </MapActionSheet>
    </>
  );
}
